import React, { useState, useEffect, useCallback } from 'react';
import { analyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { BarChart3, TrendingUp, Users, FileText, Download, Eye, Lock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';

const COLORS = ['hsl(210, 55%, 40%)', 'hsl(160, 45%, 35%)', 'hsl(38, 55%, 45%)', 'hsl(4, 65%, 45%)', 'hsl(260, 35%, 45%)', '#5B616E', '#1E5A8A', '#B89B5E'];

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [topRows, setTopRows] = useState([]);
  const [topSort, setTopSort] = useState('views');
  const [topLoading, setTopLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { articleId, data, loading }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [days]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTop(); }, [days, topSort]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ovRes, perfRes, funnelRes] = await Promise.all([
        analyticsAPI.overview(parseInt(days)),
        analyticsAPI.contentPerformance(parseInt(days)).catch(() => ({ data: null })),
        analyticsAPI.subscriptionFunnel(parseInt(days)).catch(() => ({ data: null })),
      ]);
      setOverview(ovRes.data);
      setPerformance(perfRes.data);
      setFunnel(funnelRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  const loadTop = useCallback(async () => {
    setTopLoading(true);
    try {
      const res = await analyticsAPI.topArticles(parseInt(days), 20, topSort);
      setTopRows(res.data?.rows || []);
    } catch { setTopRows([]); }
    finally { setTopLoading(false); }
  }, [days, topSort]);

  const openDetail = async (articleId) => {
    setDetail({ articleId, data: null, loading: true });
    try {
      const res = await analyticsAPI.articlePerformance(articleId, parseInt(days));
      setDetail({ articleId, data: res.data, loading: false });
    } catch {
      setDetail({ articleId, data: null, loading: false });
    }
  };

  const exportTopCsv = () => {
    if (!topRows.length) return;
    const headers = ['Title', 'Status', 'Premium', 'Views', 'Unique readers', 'Avg progress', 'Completion rate', 'Paywall shown', 'Conversions', 'Conversion rate', 'Comments'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [headers.join(',')];
    for (const r of topRows) {
      lines.push([
        esc(r.title), esc(r.status), esc(r.is_premium ? 'Yes' : 'No'),
        r.views, r.unique_readers, r.avg_progress, r.completion_rate,
        r.paywall_shown, r.paywall_converted, r.conversion_rate, r.comments,
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `neural-news-top-articles-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>;

  const kpis = overview?.kpis || {};

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-[#B89B5E]" /> Analytics</h1>
          <p className="text-sm text-[#5B616E] mt-1">Content performance and audience insights</p>
        </div>
        <Select value={days} onValueChange={setDays} data-testid="admin-analytics-range-select">
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total Views', value: kpis.total_views || 0, icon: TrendingUp, color: '#1E5A8A' },
          { label: 'Published', value: kpis.published_articles || 0, icon: FileText, color: '#1F7A5A' },
          { label: 'Users', value: kpis.total_users || 0, icon: Users, color: '#B89B5E' },
          { label: 'Subscribers', value: kpis.total_subscribers || 0, icon: Users, color: '#4A1D2A' },
        ].map((kpi, i) => (
          <Card key={i} className="bg-white border-[#E7E0D6]" data-testid="admin-kpi-card">
            <CardContent className="pt-5">
              <div className="flex items-center gap-2 text-xs text-[#5B616E] mb-2">
                <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
                {kpi.label}
              </div>
              <p className="text-2xl font-bold">{kpi.value.toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Daily Articles */}
        <Card data-testid="admin-analytics-chart" className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm font-medium">Daily Article Output</CardTitle></CardHeader>
          <CardContent>
            {overview?.daily_articles?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={overview.daily_articles}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(38, 22%, 88%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" fill="hsl(210, 55%, 40%)" fillOpacity={0.1} stroke="hsl(210, 55%, 40%)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-[250px] flex items-center justify-center text-sm text-[#5B616E]">No data</div>}
          </CardContent>
        </Card>

        {/* Category Performance */}
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm font-medium">Category Performance</CardTitle></CardHeader>
          <CardContent>
            {performance?.category_performance?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={performance.category_performance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(38, 22%, 88%)" />
                  <XAxis dataKey="category_name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="views" fill="hsl(38, 55%, 45%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[250px] flex items-center justify-center text-sm text-[#5B616E]">No data</div>}
          </CardContent>
        </Card>
      </div>

      {/* Top Authors & User Roles */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm font-medium">Top Authors</CardTitle></CardHeader>
          <CardContent>
            {performance?.author_performance?.length > 0 ? (
              <div className="space-y-3">
                {performance.author_performance.slice(0, 8).map((a, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-lg font-bold text-[#E7E0D6] w-6 text-right news-masthead">{i + 1}</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{a.author_name || 'Unknown'}</p>
                      <p className="text-xs text-[#5B616E]">{a.articles} articles · {(a.views || 0).toLocaleString()} views</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <div className="text-center py-8 text-sm text-[#5B616E]">No data</div>}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm font-medium">User Roles Distribution</CardTitle></CardHeader>
          <CardContent>
            {overview?.user_roles?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={overview.user_roles.map(r => ({ name: r.role, value: r.count }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} strokeWidth={2}>
                    {overview.user_roles.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-[250px] flex items-center justify-center text-sm text-[#5B616E]">No data</div>}
          </CardContent>
        </Card>
      </div>

      {/* Subscription funnel — visits → signups → paywall → subscribe.
          Renders each stage as a horizontal bar so drop-off between
          stages is visually obvious at a glance. */}
      {funnel?.stages?.length > 0 && (
        <Card className="bg-white border-[#E7E0D6]" data-testid="admin-subscription-funnel">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#B89B5E]" />
              Subscription funnel
              <span className="ml-auto text-[10px] text-[#5B616E] font-normal">
                Overall conversion {(funnel.overall_conversion * 100).toFixed(2)}%
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {funnel.stages.map((s, i) => {
                const top = funnel.stages[0]?.count || 1;
                const pctTop = Math.max(2, (s.count / top) * 100);
                const stageColor = ['#1E5A8A', '#4A90D9', '#B89B5E', '#9A6B1A', '#1F7A5A'][i] || '#5B616E';
                return (
                  <div key={s.key}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium">{s.label}</span>
                      <span className="text-[#5B616E]">
                        {s.count.toLocaleString()}
                        {i > 0 && (
                          <span className="ml-2 text-[10px] text-[#B89B5E]">
                            {(s.conversion_from_previous * 100).toFixed(1)}% from previous
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="w-full h-2.5 rounded-full bg-[#F5F0E6] overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pctTop}%`, background: stageColor }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top articles leaderboard — ranked table with drill-in to the
          per-article performance modal and CSV export. */}
      <Card className="bg-white border-[#E7E0D6]" data-testid="admin-top-articles">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1E5A8A]" />
            Top articles
            <div className="ml-auto flex items-center gap-2">
              <Select value={topSort} onValueChange={setTopSort}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid="admin-top-articles-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="views">Views</SelectItem>
                  <SelectItem value="unique_readers">Unique readers</SelectItem>
                  <SelectItem value="completion_rate">Completion rate</SelectItem>
                  <SelectItem value="conversion_rate">Paywall conv.</SelectItem>
                  <SelectItem value="comments">Comments</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={exportTopCsv}
                disabled={!topRows.length}
                data-testid="admin-top-articles-export"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topLoading ? (
            <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 rounded" />)}</div>
          ) : topRows.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#5B616E]">No articles in this window</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="admin-top-articles-table">
                <thead className="text-[#5B616E] border-b border-[#E7E0D6]">
                  <tr className="text-left">
                    <th className="py-2 pr-3 font-medium">#</th>
                    <th className="py-2 pr-3 font-medium">Title</th>
                    <th className="py-2 pr-3 font-medium text-right">Views</th>
                    <th className="py-2 pr-3 font-medium text-right">Unique</th>
                    <th className="py-2 pr-3 font-medium text-right">Complete</th>
                    <th className="py-2 pr-3 font-medium text-right">Conv.</th>
                    <th className="py-2 pr-3 font-medium text-right">Comments</th>
                    <th className="py-2 pl-3 font-medium text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {topRows.map((r, i) => (
                    <tr key={r.article_id} className="border-b border-[#F5F0E6] hover:bg-[#FAF7F0]">
                      <td className="py-2 pr-3 text-[#B89B5E] font-semibold">{i + 1}</td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          {r.is_premium && <Lock className="w-3 h-3 text-[#B89B5E] shrink-0" />}
                          <span className="font-medium truncate max-w-[360px]" title={r.title}>{r.title || 'Untitled'}</span>
                        </div>
                        <div className="text-[10px] text-[#5B616E] mt-0.5 capitalize">{r.status}</div>
                      </td>
                      <td className="py-2 pr-3 text-right font-medium">{(r.views || 0).toLocaleString()}</td>
                      <td className="py-2 pr-3 text-right">{(r.unique_readers || 0).toLocaleString()}</td>
                      <td className="py-2 pr-3 text-right">{((r.completion_rate || 0) * 100).toFixed(1)}%</td>
                      <td className="py-2 pr-3 text-right">
                        {r.paywall_shown > 0 ? `${((r.conversion_rate || 0) * 100).toFixed(1)}%` : <span className="text-[#5B616E]">—</span>}
                      </td>
                      <td className="py-2 pr-3 text-right">{r.comments || 0}</td>
                      <td className="py-2 pl-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2"
                          onClick={() => openDetail(r.article_id)}
                          data-testid="admin-top-articles-open-detail"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-article performance detail modal — driven by the composite
          /analytics/articles/{id}/performance endpoint. */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-article-performance-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {detail?.data?.article?.title || 'Article performance'}
            </DialogTitle>
          </DialogHeader>
          {detail?.loading ? (
            <div className="space-y-3 py-4">
              <Skeleton className="h-10" />
              <Skeleton className="h-40" />
              <Skeleton className="h-24" />
            </div>
          ) : !detail?.data ? (
            <div className="py-8 text-center text-sm text-[#5B616E]">Failed to load performance data.</div>
          ) : (
            <ArticlePerformanceDetail data={detail.data} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small presentational component that renders the article performance
// payload as a grid of KPI tiles, a sparkline of daily views, the paywall
// funnel bars, and a device split donut.
function ArticlePerformanceDetail({ data }) {
  const { article, daily_views = [], device_split = [], unique_readers, reading, paywall, engagement } = data;
  const fmtPct = (v) => `${((v || 0) * 100).toFixed(1)}%`;
  const kpis = [
    { label: 'Views (window)', value: daily_views.reduce((s, d) => s + (d.views || 0), 0).toLocaleString() },
    { label: 'Views (all time)', value: (article?.views_total || 0).toLocaleString() },
    { label: 'Unique readers', value: (unique_readers || 0).toLocaleString() },
    { label: 'Avg progress', value: fmtPct(reading?.avg_progress) },
    { label: 'Completion rate', value: fmtPct(reading?.completion_rate) },
    { label: 'Comments', value: (engagement?.comments || 0).toLocaleString() },
    { label: 'Shares', value: (engagement?.shares || 0).toLocaleString() },
    { label: 'Paywall conv.', value: paywall?.shown ? fmtPct(paywall.conversion_rate) : '—' },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {kpis.map((k, i) => (
          <div key={i} className="p-2.5 rounded-md bg-[#FAF7F0] border border-[#E7E0D6]">
            <div className="text-[10px] text-[#5B616E]">{k.label}</div>
            <div className="text-base font-semibold">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-xs font-medium">Daily views</CardTitle></CardHeader>
          <CardContent>
            {daily_views.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={daily_views}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(38, 22%, 88%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => (d || '').slice(5)} />
                  <YAxis tick={{ fontSize: 9 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="views" fill="hsl(210, 55%, 40%)" fillOpacity={0.15} stroke="hsl(210, 55%, 40%)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-[180px] flex items-center justify-center text-xs text-[#5B616E]">No views yet</div>}
          </CardContent>
        </Card>
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-xs font-medium">Device split</CardTitle></CardHeader>
          <CardContent>
            {device_split.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={device_split.map(d => ({ name: d.device, value: d.count }))} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} strokeWidth={2}>
                    {device_split.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-[180px] flex items-center justify-center text-xs text-[#5B616E]">No device data</div>}
          </CardContent>
        </Card>
      </div>
      {(paywall?.shown || 0) > 0 && (
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-xs font-medium">Paywall funnel</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { label: 'Paywall shown', count: paywall.shown, color: '#1E5A8A' },
                { label: 'Tapped subscribe', count: paywall.cta_clicked, color: '#B89B5E' },
                { label: 'Converted', count: paywall.converted, color: '#1F7A5A' },
              ].map((s, i) => {
                const top = paywall.shown || 1;
                const pct = Math.max(2, (s.count / top) * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="font-medium">{s.label}</span>
                      <span className="text-[#5B616E]">{(s.count || 0).toLocaleString()}</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-[#F5F0E6] overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
